import React, { useState, useEffect } from 'react';
import { checkRateLimit, updateRateLimit, RATE_LIMIT_KEYS } from '../utils/rateLimiter';
import { trackFormSubmission, trackError } from '../utils/analytics';
import { submitNetlifyForm } from '../utils/netlifyFormSubmit';
import './ContactForm.css';

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      const { remainingTime } = checkRateLimit(RATE_LIMIT_KEYS.CONTACT_FORM);
      setRemainingTime(remainingTime);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setFormError('');

    const { allowed, remainingTime } = checkRateLimit(RATE_LIMIT_KEYS.CONTACT_FORM);
    if (!allowed) {
      setFormError(`Please wait ${remainingTime} seconds before submitting again.`);
      trackError('validation', 'Rate limit exceeded', 'ContactForm');
      return;
    }

    const formData = new FormData(e.target);
    const name = String(formData.get('name') || '').trim();
    const email = String(formData.get('email') || '').trim();
    const message = String(formData.get('message') || '').trim();

    if (name.length < 1) {
      setFormError('Please enter your name.');
      trackError('validation', 'Name missing', 'ContactForm');
      return;
    }

    if (message.length < 10) {
      setFormError('Message must be at least 10 characters long.');
      trackError('validation', 'Message too short', 'ContactForm');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setFormError('Please enter a valid email address.');
      trackError('validation', 'Invalid email format', 'ContactForm');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitNetlifyForm(e.target, 'contact');
      updateRateLimit(RATE_LIMIT_KEYS.CONTACT_FORM);
      trackFormSubmission('contact', true);
      setSubmitted(true);
    } catch (err) {
      trackError('submit', err.message || 'submit failed', 'ContactForm');
      setFormError('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="full-page-wrapper" style={{ background: 'linear-gradient(to left bottom, #777777, #1a1a1a)', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
        <div className="app-theme-contact-form" style={{ paddingTop: 'var(--spacing-lg, 30px)' }}>
          <div className="success-message">
            <h3>Thank you for your message!</h3>
            <p>We'll get back to you soon.</p>
            <button onClick={() => setSubmitted(false)} disabled={remainingTime > 0}>
              {remainingTime > 0
                ? `Wait ${remainingTime}s to send another message`
                : 'Send another message'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="full-page-wrapper" style={{ background: 'linear-gradient(to left bottom, #777777, #1a1a1a)', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', width: '100%', paddingTop: '20px' }}>
      <div className="app-theme-contact-form" style={{ paddingTop: '0px' }}>
        <h1 style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg, 30px)', marginTop: '0px', color: 'var(--text-color, white)' }}>Contact Us</h1>
        <form
          name="contact"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          className="contact-form-container"
        >
          <input type="hidden" name="form-name" value="contact" />
          <p hidden>
            <label>
              Don't fill this out if you're human: <input name="bot-field" tabIndex={-1} autoComplete="off" />
            </label>
          </p>

          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label htmlFor="name">Name:</label>
            <input type="text" id="name" name="name" required />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input type="email" id="email" name="email" required />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message:</label>
            <textarea id="message" name="message" required></textarea>
          </div>

          <button
            type="submit"
            disabled={remainingTime > 0 || isSubmitting}
            className={remainingTime > 0 || isSubmitting ? 'disabled' : ''}
          >
            {remainingTime > 0 ? `Wait ${remainingTime}s` : isSubmitting ? 'Sending…' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ContactForm;
